"use client";

import { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import Link from 'next/link';
import { Footer } from '@/components/Footer';

// Content Data
const contentData = {
    onboarding: {
        title: "01 User Onboarding & Baseline",
        theory: "Theory: Self-Monitoring Concept",
        theoryDesc: "การเฝ้าสังเกตตนเอง (Self-monitoring) เป็นพื้นฐานของการปรับเปลี่ยนพฤติกรรม การจดบันทึก 'ตามจริง' ช่วยเพิ่มความตระหนักรู้ (Awareness) ของระดับความเสพติดก่อนการเริ่มต้น",
        steps: [
            { icon: "🔐", title: "Authentication", desc: "ยืนยันตัวตนเพื่อเริ่มสร้างพื้นที่ปลอดภัยส่วนบุคคล" },
            { icon: "📊", title: "Automatic Baseline", desc: "ระบบไม่กำหนดตัวเลขให้ แต่จะ 'เรียนรู้' จากพฤติกรรมจริงของคุณในวันแรก เพื่อความแม่นยำทางสถิติ" }
        ]
    },
    tracking: {
        title: "02 Daily Interaction & Neuro-Stability",
        theory: "Theory: Operant Conditioning",
        theoryDesc: "ใช้การตอบสนองแบบทันที (Real-time feedback) เพื่อสร้างพฤติกรรมใหม่ การจำกัดโควตาทีละนิดช่วยรักษาระดับโดพามีนในสมองไม่ให้ลดต่ำเกินไปจนเกิดอาการหงุดหงิด",
        steps: [
            { icon: "🧮", title: "Dynamic Quota", desc: "โควตาถูกคำนวณแบบ Adaptive ตามวันที่อยู่ในโปรแกรม" },
            { icon: "🌬️", title: "Crave Mode (4-7-8)", desc: "ใช้หลักการ Vagus Nerve Stimulation เพื่อเปลี่ยนสภาวะอารมณ์จากสู้/หนี (Sympathetic) เป็นสภาวะผ่อนคลาย (Parasympathetic)" }
        ]
    },
    penalty: {
        title: "03 Penalty & Behavioral Accountability",
        theory: "Theory: Behavioral Economics - Loss Aversion",
        theoryDesc: "มนุษย์จะพยายามหลีกเลี่ยงการสูญเสียมากกว่าการได้มา ระบบ Hard Reset สร้างต้นทุนทางจิตวิทยา (Psychological Cost) ให้ผู้ใช้รักษาวินัยมากขึ้น เพื่อไม่ให้ 'วันที่สำเร็จ' ต้องกลับไปนับหนึ่งใหม่",
        steps: [
            { icon: "🚨", title: "Hard Reset Trigger", desc: "หากสูบเกิน ระบบจะถือว่าพฤติกรรมปัจจุบันเปลี่ยนไป และรีเซ็ตโควตาตามความจริงล่าสุด" },
            { icon: "🔄", title: "New Start Date", desc: "เริ่มนับวันที่ 1 ใหม่ทันที เพื่อปรับร่างกายให้ชินจากฐานใหม่ที่มั่นคง" }
        ]
    },
    analytics: {
        title: "04 Analytics & Positive Reinforcement",
        theory: "Theory: Neuroplasticity & Habit Loop",
        theoryDesc: "การเชื่อมโยง 'ความสำเร็จของการเลิก' เข้ากับ 'รางวัลที่ต้องการ' (Saving Goals) ช่วยกระตุ้นระบบการให้รางวัลในสมอง (Reward System) ให้ทำงานทดแทนการเสพนิโคติน",
        steps: [
            { icon: "💰", title: "Reward Tracking", desc: "คำนวณเงินออมสะสมเพื่อให้เห็นมูลค่าที่จับต้องได้ของการชนะใจตนเอง" },
            { icon: "📈", title: "Visual Progress", desc: "กราฟแนวโน้มขาลงช่วยตอกย้ำความเชื่อมั่นในตนเอง (Self-Efficacy)" }
        ]
    }
};

type TabKey = keyof typeof contentData;

export default function ScienceExplorer() {
    const [activeTab, setActiveTab] = useState<TabKey>('onboarding');
    const [baseline, setBaseline] = useState(12);
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<Chart | null>(null);

    // Initial Data
    const currentData = contentData[activeTab];

    useEffect(() => {
        if (!chartRef.current) return;

        const ctx = chartRef.current.getContext('2d');
        if (!ctx) return;

        // Destroy previous instance
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // Calculate Data
        const schedule = [];
        const labels = [];
        let currentQuota = baseline;
        let day = 1;

        while (currentQuota >= 0) {
            const reduction = Math.floor((day - 1) / 3);
            currentQuota = Math.max(0, baseline - reduction);
            labels.push(`Day ${day}`);
            schedule.push(currentQuota);
            if (currentQuota === 0) break;
            day++;
        }

        // Create Chart
        chartInstance.current = new Chart(ctx, {
            type: 'line',
            data: {
                labels: labels,
                datasets: [{
                    label: 'นิโคตินโควตา (มวน)',
                    data: schedule,
                    borderColor: '#60a5fa',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    borderWidth: 4,
                    pointBackgroundColor: '#2563eb',
                    pointBorderColor: '#fff',
                    pointRadius: 5,
                    fill: true,
                    stepped: true
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                    y: {
                        beginAtZero: true,
                        grid: { color: 'rgba(255,255,255,0.05)' },
                        ticks: { color: '#64748b', font: { weight: 'bold' } }
                    },
                    x: {
                        grid: { display: false },
                        ticks: { color: '#64748b', maxTicksLimit: 12 }
                    }
                }
            } as any
        });

        // Cleanup
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [baseline]);

    const handleAdjustBaseline = (amount: number) => {
        setBaseline(prev => Math.max(1, Math.min(60, prev + amount)));
    };

    return (
        <div className="min-h-screen bg-slate-50 text-slate-800 antialiased font-[family-name:var(--font-sarabun)]">
            {/* Navigation */}
            <nav className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-slate-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16 items-center">
                        <Link href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                            <span className="text-3xl">🔥</span>
                            <span className="font-extrabold text-xl tracking-tight text-slate-900">SmokeFree Pro <span className="text-blue-600 font-medium text-sm">Science Explorer</span></span>
                        </Link>
                        <div className="hidden md:flex space-x-8">
                            <a href="#theory" className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors">ทฤษฎีหลัก</a>
                            <a href="#workflow" className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors">Workflow</a>
                            <a href="#simulator" className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors">Simulator</a>
                            <a href="#tech" className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors">Architecture</a>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Header Section */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
                <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6 border border-blue-100">
                    Validated Methodology
                </div>
                <h1 className="text-4xl sm:text-6xl font-black text-slate-900 mb-6 leading-tight">
                    ผสานจิตวิทยาและวิทยาศาสตร์ <br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">เพื่อการเลิกบุหรี่ที่ได้ผลจริง</span>
                </h1>
                <p className="text-lg text-slate-500 max-w-3xl mx-auto mb-12">
                    ทำไมแอปของเราถึงแตกต่าง? เพราะทุกฟีเจอร์ถูกสร้างขึ้นบนพื้นฐานงานวิจัยด้านการปรับเปลี่ยนพฤติกรรม (Behavioral Change) และกลไกทางประสาทวิทยา (Neuroscience)
                </p>
            </section>

            {/* Main Theory Grid */}
            <section id="theory" className="max-w-7xl mx-auto px-4 pb-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 transition-all hover:translate-y-[-5px] hover:shadow-lg">
                    <div className="text-2xl mb-3">🧠</div>
                    <h4 className="font-bold text-slate-900 mb-1">Stages of Change</h4>
                    <p className="text-xs text-slate-500">อ้างอิงทฤษฎี TTM โดย Prochaska พัฒนาระบบให้สอดคล้องกับระยะการเตรียมตัวและลงมือทำจริง</p>
                </div>
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 transition-all hover:translate-y-[-5px] hover:shadow-lg">
                    <div className="text-2xl mb-3">⚖️</div>
                    <h4 className="font-bold text-slate-900 mb-1">Loss Aversion</h4>
                    <p className="text-xs text-slate-500">จิตวิทยาเศรษฐศาสตร์พฤติกรรม มนุษย์กลัวความสูญเสียความก้าวหน้า (Reset) มากกว่าความพยายามที่ได้รับ</p>
                </div>
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 transition-all hover:translate-y-[-5px] hover:shadow-lg">
                    <div className="text-2xl mb-3">🧪</div>
                    <h4 className="font-bold text-slate-900 mb-1">Receptor Downregulation</h4>
                    <p className="text-xs text-slate-500">การค่อยๆ ลดนิโคตินช่วยให้ตัวรับนิโคตินในสมองลดลงโดยไม่เกิดภาวะ Shock ต่อระบบประสาท</p>
                </div>
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 transition-all hover:translate-y-[-5px] hover:shadow-lg">
                    <div className="text-2xl mb-3">🌬️</div>
                    <h4 className="font-bold text-slate-900 mb-1">Vagus Nerve Stimulation</h4>
                    <p className="text-xs text-slate-500">การหายใจ 4-7-8 กระตุ้นเส้นประสาทเวกัส เพื่อระงับการหลั่งอะดรีนาลีนที่เกิดจากความอยาก (Craving)</p>
                </div>
            </section>

            {/* Workflow Section */}
            <section id="workflow" className="bg-white py-20 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-black text-slate-900">กระบวนการที่ขับเคลื่อนด้วยหลักการ</h2>
                        <p className="text-slate-500 mt-2 italic">คลิกแต่ละระยะเพื่อดูทฤษฎีที่อยู่เบื้องหลัง</p>
                    </div>

                    {/* Tab Navigation */}
                    <div className="flex flex-wrap justify-center gap-2 mb-10">
                        {(Object.keys(contentData) as TabKey[]).map((key) => (
                            <button
                                key={key}
                                onClick={() => setActiveTab(key)}
                                className={`px-6 py-3 text-sm font-bold transition-all rounded-full border border-transparent 
                                    ${activeTab === key
                                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 border-blue-600'
                                        : 'hover:bg-slate-50 text-slate-500'}`}
                            >
                                {contentData[key].title}
                            </button>
                        ))}
                    </div>

                    <div className="bg-slate-50 rounded-[3rem] p-8 md:p-12 border border-slate-200 min-h-[450px]">
                        <div className="animate-in slide-in-from-bottom-4 duration-500">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                                <div className="space-y-6">
                                    <h3 className="text-3xl font-black text-slate-900 italic tracking-tighter">{currentData.title}</h3>
                                    <div className="p-6 bg-white border border-blue-100 rounded-[2rem] shadow-sm relative overflow-hidden">
                                        <div className="absolute -right-4 -top-4 w-12 h-12 bg-blue-50 rounded-full opacity-50"></div>
                                        <span className="bg-[#eff6ff] text-[#1d4ed8] px-2 py-0.5 rounded text-[0.65rem] font-bold uppercase border border-[#dbeafe] mb-3 inline-block">
                                            {currentData.theory}
                                        </span>
                                        <p className="text-slate-600 text-sm leading-relaxed">{currentData.theoryDesc}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-4">
                                    {currentData.steps.map((step, idx) => (
                                        <div key={idx} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-start gap-4 transition-all hover:translate-y-[-2px]">
                                            <div className="text-2xl">{step.icon}</div>
                                            <div>
                                                <h4 className="font-bold text-slate-900 text-sm mb-1 uppercase">{step.title}</h4>
                                                <p className="text-xs text-slate-500 leading-relaxed">{step.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Tapering Simulator */}
            <section id="simulator" className="py-20 px-4 max-w-6xl mx-auto">
                <div className="bg-slate-900 rounded-[3rem] p-8 md:p-16 text-white shadow-2xl relative overflow-hidden">
                    <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <span className="bg-[#eff6ff] text-[#1d4ed8] px-2 py-0.5 rounded text-[0.65rem] font-bold uppercase border border-[#dbeafe] mb-4 inline-block">
                                Physiological Plateau Algorithm
                            </span>
                            <h2 className="text-3xl font-black mb-4 leading-tight">ทำไมต้อง "3 วันลด 1 มวน"?</h2>
                            <p className="text-slate-400 text-sm leading-relaxed mb-8">
                                งานวิจัยด้านพิษวิทยาพบว่า ร่างกายต้องการเวลาเฉลี่ย 48-72 ชั่วโมงในการปรับสมดุลเคมีในเลือดใหม่เมื่อปริมาณนิโคตินลดลง
                                การลดเร็วกว่านี้จะเพิ่มอัตราการล้มเหลว (Relapse Rate) ระบบของเราจึงใช้สมการ 3-Day Step-Down เพื่อรักษาเสถียรภาพของโดพามีนในสมอง
                            </p>
                            <div className="bg-white/10 p-6 rounded-2xl border border-white/10">
                                <label className="block text-xs font-bold text-blue-300 uppercase mb-3">ทดลองจำลองแผนการลดของคุณ</label>
                                <div className="flex items-center gap-6">
                                    <button
                                        onClick={() => handleAdjustBaseline(-1)}
                                        className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center font-bold text-xl transition-all border border-white/10"
                                    >-</button>
                                    <span className="text-6xl font-black font-mono w-24 text-center">{baseline}</span>
                                    <button
                                        onClick={() => handleAdjustBaseline(1)}
                                        className="w-12 h-12 bg-blue-600 hover:bg-blue-500 rounded-full flex items-center justify-center font-bold text-xl transition-all shadow-lg shadow-blue-600/50"
                                    >+</button>
                                </div>
                            </div>
                        </div>
                        <div className="bg-black/20 rounded-3xl p-6 border border-white/5 h-[350px] w-full">
                            <div className="relative w-full h-full">
                                <canvas ref={chartRef}></canvas>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Architecture Section */}
            <section id="tech" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-black text-slate-900 uppercase italic">Data-Driven Privacy</h2>
                        <p className="text-slate-500 mt-2">สถาปัตยกรรมที่ออกแบบมาเพื่อความปลอดภัยของข้อมูลพฤติกรรม</p>
                    </div>

                    <div className="flex flex-col md:flex-row justify-center items-stretch gap-4 text-center">
                        <div className="flex-1 bg-slate-50 p-6 rounded-2xl border border-slate-200">
                            <div className="text-3xl mb-3">🛡️</div>
                            <h5 className="font-bold text-sm">Google Auth</h5>
                            <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">Identity Management: ยืนยันตัวตนผ่านมาตรฐาน OAuth 2.0 เพื่อความปลอดภัยสูงสุด</p>
                        </div>
                        <div className="flex-1 bg-blue-50 p-6 rounded-2xl border border-blue-200">
                            <div className="text-3xl mb-3">⚡</div>
                            <h5 className="font-bold text-sm">Next.js Engine</h5>
                            <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">Server-Side Logic: ประมวลผลตรรกะ Penalty และโควตาทันทีที่ฝั่งเซิร์ฟเวอร์</p>
                        </div>
                        <div className="flex-1 bg-slate-800 p-6 rounded-2xl text-white shadow-xl">
                            <div className="text-3xl mb-3">🐘</div>
                            <h5 className="font-bold text-sm">PostgreSQL</h5>
                            <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">Relational Persistence: เก็บ Log มวนต่อมวนแบบ Transactional เพื่อสถิติที่แม่นยำ 100%</p>
                        </div>
                    </div>
                </div>
            </section>


            <Footer theme="light" />
        </div>
    );
}
